import type { Client, CoachProfile, UserGroup } from '@efdigital/ll-addon-data';
import type { Coaching } from '@efdigital/ll-addon-data/types/coaching/coaching';
import type { CoachingConfig } from '@efdigital/ll-addon-data/types/coaching/coaching-config';
import { findUserGroup } from '@efdigital/ll-admin-v2/data/builders/system/user-groups';
import { findAllCoachProfiles } from '@efdigital/ll-admin-v2/data/builders/xceed/coach-profiles';
import { findAllCoachingConfigs } from '@efdigital/ll-admin-v2/data/builders/xceed/coaching-configs';
import { findCoachingPlansByCoachingId } from '@efdigital/ll-admin-v2/data/builders/xceed/coaching-plans';
import { findCoaching } from '@efdigital/ll-admin-v2/data/builders/xceed/coachings';
import { generateCoachPool } from '@efdigital/ll-admin-v2/data/generators/coaching-generator.ts';
import { getPaginationTotal } from '@efdigital/ll-admin-v2/helpers/get-pagination-total';
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

import type { ProgramsProgramCohortModel } from '../cohort.ts';

export type ProgramsProgramCohortCoachingModel = ProgramsProgramCohortModel & {
  coaching: Coaching | null;
  quizId: string | null;
  coachingConfigs: CoachingConfig[];
  coachPool: UserGroup | null;
  coaches: CoachProfile[];
  client: Client | null;
  enrolledCount: number;
};

export default class ProgramsProgramCohortCoachingRoute extends Route<ProgramsProgramCohortCoachingModel> {
  @service declare store: Store;

  async model() {
    const parentModel = this.modelFor(
      'programs.program.cohort'
    ) as ProgramsProgramCohortModel;
    const { cohort } = parentModel;

    let coaching: Coaching | null = null;
    try {
      const coachingRef = cohort.belongsTo('coaching' as never);
      const coachingId = coachingRef.id();
      if (coachingId) {
        coaching = (await this.store.request(findCoaching(coachingId))).content
          .data;
      }
    } catch {
      coaching = null;
    }

    // Read relationship IDs synchronously — avoids reactive access which would
    // trigger Warp-Drive auto-fetches using the wrong (app-host) URL.
    type HasBelongsToRef = {
      belongsTo(rel: string): { id(): string | null };
    };
    const coachPoolId = coaching
      ? (coaching as unknown as HasBelongsToRef).belongsTo('coachPool').id()
      : null;

    // Resolve pool and client in parallel using explicit builders (correct API host).
    const [rawPool, cohortClient] = await Promise.all([
      coachPoolId
        ? this.store
            .request(findUserGroup(coachPoolId))
            .then((r) => r.content.data)
            .catch(() => null)
        : Promise.resolve(null),
      (cohort.client as unknown as Promise<Client | null>).catch(() => null),
    ]);

    // Every coaching must have a pool — auto-provision one if missing.
    let coachPool = rawPool ?? null;
    if (coaching && !coachPool) {
      coachPool = await generateCoachPool(cohort, coaching, {
        store: this.store,
      });
    }

    const [configsResult, coachesResult, plansCountResult] = await Promise.all([
      this.store.request(findAllCoachingConfigs()).catch(() => null),
      this.store.request(findAllCoachProfiles()).catch(() => null),
      // A single record is enough — we only read the total from page meta.
      coaching?.id
        ? this.store
            .request(
              findCoachingPlansByCoachingId(coaching.id, {
                offset: 0,
                limit: 1,
              })
            )
            .catch(() => null)
        : Promise.resolve(null),
    ]);

    // Read the quiz ID from relationship data synchronously — avoids a reactive
    // access to coaching.quiz which would trigger Warp-Drive to auto-fetch the
    // quiz record with a URL that bypasses the API host config.
    type HasBelongsTo = { belongsTo(rel: string): { id(): string | null } };
    const quizId = coaching
      ? (coaching as unknown as HasBelongsTo).belongsTo('quiz').id()
      : null;

    return {
      ...parentModel,
      coaching,
      quizId,
      coachingConfigs: configsResult?.content.data ?? [],
      coachPool,
      coaches: coachesResult?.content.data ?? [],
      client: cohortClient,
      enrolledCount: plansCountResult
        ? getPaginationTotal(plansCountResult.content)
        : 0,
    };
  }
}
